import {
  Box,
  Button,
  Flex,
  Heading,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spacer,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { conversationAction } from 'actions';
import DeleteModal from 'components/Modal/DeleteModal';
import EditModal from 'components/Modal/EditModal';
import { TEMPORARY } from 'helpers/constant';
import { timeFormat } from 'helpers/date';
import { VIEW_MODE } from 'helpers/enum';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiPlusMedical } from 'react-icons/bi';
import { BsChatLeftDots, BsThreeDotsVertical } from 'react-icons/bs';
import { PiSidebarSimpleBold } from 'react-icons/pi';
import { useDispatch, useSelector } from 'react-redux';
import { Conversation } from 'types/reducers/conversation';

interface ConversationListProps {
  onAddNew: () => void;
  onChange: (_conversation: Conversation) => void;
  onCloseSidebar: () => void;
}

const ConversationsList = ({ onAddNew, onChange, onCloseSidebar }: ConversationListProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { conversations, current, selectedCitation, viewMode, processingStreamingChunkStatus } =
    useSelector((state) => state.conversations);
  const { data, total_page, current_page } = conversations;

  const [recordDelete, setRecordDelete] = useState<Conversation>();
  const [recordEdit, setRecordEdit] = useState<Conversation>();

  const { isOpen: isToggleSidebar, onToggle: onToggleSidebar } = useDisclosure({
    defaultIsOpen: true,
  });
  const {
    isOpen: isOpenEditDialog,
    onOpen: onOpenEditDialog,
    onClose: onCloseEditDialog,
  } = useDisclosure();
  const {
    isOpen: isOpenDeleteDialog,
    onOpen: onOpenDeleteDialog,
    onClose: onCloseDeleteDialog,
  } = useDisclosure();

  const isShowCitation = Boolean(viewMode === VIEW_MODE.PREVIEW_FILE && selectedCitation?.file_url);
  const isShow = isToggleSidebar && !isShowCitation;
  const selectedConversationId = current?.conversation_id;
  const isStreaming = processingStreamingChunkStatus ? 'not-allowed' : 'pointer';

  const onShowMore = () => {
    dispatch(
      conversationAction.getConversations({
        page: current_page + 1,
        size: 10,
      })
    );
  };

  const onClickDelete = (conversation: Conversation) => {
    setRecordDelete(conversation);
    onOpenDeleteDialog();
  };

  const onClickEdit = (conversation: Conversation) => {
    setRecordEdit(conversation);
    onOpenEditDialog();
  };

  const handleDelete = (conversation: Conversation) => {
    dispatch(
      conversationAction.deleteConversation({
        id: conversation.id,
      })
    );
  };

  const handleEdit = (updatedConversation: Conversation & { new_name: string }) => {
    dispatch(
      conversationAction.editConversationName({
        id: updatedConversation.id,
        new_name: updatedConversation.new_name,
      })
    );
  };

  useEffect(() => {
    if (current?.conversation_id) {
      dispatch(conversationAction.updateViewMode(VIEW_MODE.CHAT));
      dispatch(conversationAction.getDetailConversation(current));
    }
  }, [current?.conversation_id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <DeleteModal
        isOpen={isOpenDeleteDialog}
        onClose={onCloseDeleteDialog}
        recordDelete={recordDelete}
        action={handleDelete}
        dataHeader={t('chat.deleteConversation')}
        dataBody={t('chat.deleteConversationInstruction')}
        btnAction={t('common.delete')}
        color='red'
      />
      <EditModal
        isOpen={isOpenEditDialog}
        onClose={onCloseEditDialog}
        recordEdit={recordEdit}
        action={handleEdit}
        dataHeader={t('chat.renameConversation')}
        btnAction={t('chat.rename')}
      />
      <Box
        pos={'relative'}
        w={{ base: '100%', lg: `${isShow ? '320px' : '80px'}` }}
        minW={{ base: '100%', lg: `${isShow ? '320px' : '80px'}` }}
        maxW={'320px'}
        h={{ base: '100%', lg: 'calc(100vh - 175px)' }}
        bg={{ lg: 'bg_main_container' }}
        borderRadius={{ lg: '20px' }}
        shadow={{ lg: `${isShow ? 'shadow_box' : 'none'}` }}
        transition='width .05s ease-in-out'
      >
        <Box
          overflow={{ lg: `${isShow ? 'auto' : 'hidden'}` }}
          w={{ base: '100%', lg: `${isShow ? '320px' : '0px'}` }}
          maxH='calc(100% - 100px)'
          p={isShow ? '16px' : '0px'}
          visibility={isShow ? 'visible' : 'hidden'}
        >
          <Flex align='center'>
            <Heading as='h3' mr='1rem' fontWeight={'bold'} size='md'>
              {t('chat.conversations')}
            </Heading>
            <Spacer />
            <Tooltip label={t('common.addNew')}>
              <Button
                pos='relative'
                w='40px'
                h='40px'
                bg='grey.800'
                borderRadius='50%'
                _hover={{}}
                _focus={{}}
                cursor={isStreaming}
                isDisabled={data?.[0]?.type === TEMPORARY}
                onClick={onAddNew}
              >
                <BiPlusMedical
                  color='white'
                  style={{
                    position: 'absolute',
                    top: '12px',
                    left: '12px',
                    zIndex: '999',
                  }}
                />
                <Box pos='absolute' w='40px' h='40px' bg='#7963FF' borderRadius='50%' />
              </Button>
            </Tooltip>
          </Flex>

          <Box mt={5} mb={3} fontSize={12}>
            {t('common.recent')}
          </Box>
          <Box>
            {data?.map((item) => (
              <Box
                key={item.id}
                pos='relative'
                h='60px'
                my={2}
                p='12px 14px 20px 20px'
                fontSize={'14px'}
                fontWeight={selectedConversationId === item.id ? '600' : ''}
                textAlign='left'
                bg={
                  selectedConversationId === item.id
                    ? 'bg_conversation_item_select'
                    : 'bg_conversation_item'
                }
                borderRadius='10px'
                shadow={
                  selectedConversationId === item.id
                    ? 'shadow_box'
                    : 'rgba(0, 0, 0, 0.05) 0px 0px 0px 1px'
                }
                _hover={{
                  bg: 'bg_conversation_item_select',
                  boxShadow: 'shadow_box',
                }}
                cursor={isStreaming}
                onClick={() => {
                  onChange(item);
                  onCloseSidebar();
                }}
              >
                <Flex align='center' justify='between'>
                  <Box w='20px'>
                    <BsChatLeftDots size={20} />
                  </Box>
                  <Tooltip label={item.name} placement='top-start'>
                    <Box
                      overflow='hidden'
                      maxW='160px'
                      ms='12px'
                      whiteSpace='nowrap'
                      textOverflow='ellipsis'
                    >
                      {item.name}
                    </Box>
                  </Tooltip>
                  <Spacer />
                  <Box w='20px'>
                    <ConversationOptions
                      data={item}
                      onClickDelete={onClickDelete}
                      onClickEdit={onClickEdit}
                    />
                  </Box>
                </Flex>
                <Box pos='absolute' right='42px' bottom='4px' fontSize='12px'>
                  {timeFormat(item?.last_updated)}
                </Box>
              </Box>
            ))}
          </Box>

          {current_page + 1 < total_page && (
            <Box
              fontSize='12px'
              textAlign='right'
              textDecoration='underline'
              cursor='pointer'
              onClick={onShowMore}
            >
              {t('common.moreHistory')}
            </Box>
          )}
        </Box>
        <Tooltip
          display={{ base: 'none', md: 'none', lg: 'block' }}
          label={isShow ? t('common.close') : t('common.open')}
          placement='right'
        >
          <Button
            pos='absolute'
            bottom={5}
            left={5}
            display={{ base: 'none', md: 'none', lg: 'block' }}
            color='grey.50'
            isDisabled={isShowCitation}
            onClick={onToggleSidebar}
            variant='submit'
          >
            <PiSidebarSimpleBold />
          </Button>
        </Tooltip>
      </Box>
    </>
  );
};

interface ConversationOptionsProps {
  data: Conversation;
  onClickDelete: (_data: Conversation) => void;
  onClickEdit: (_data: Conversation) => void;
}

const ConversationOptions = ({ data, onClickDelete, onClickEdit }: ConversationOptionsProps) => {
  const { t } = useTranslation();
  return (
    <Menu>
      <MenuButton
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <BsThreeDotsVertical />
      </MenuButton>
      <MenuList minW='100px' p={0} bg='transparent' borderRadius={0}>
        {data?.type !== TEMPORARY && (
          <MenuItem
            bg='menu_item_option'
            _hover={{ bg: 'menu_item_option_hover' }}
            onClick={(e) => {
              e.stopPropagation();
              onClickEdit(data);
            }}
          >
            {t('common.edit')}
          </MenuItem>
        )}
        <MenuItem
          bg='menu_item_option'
          _hover={{ bg: 'menu_item_option_hover' }}
          onClick={(e) => {
            e.stopPropagation();
            onClickDelete(data);
          }}
        >
          {t('common.delete')}
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

export default ConversationsList;
